import Vue from 'vue'

import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'

/* Plugins */

import nuxt_plugin_sentryserver_42784c19 from 'nuxt_plugin_sentryserver_42784c19' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_46824ade from 'nuxt_plugin_sentryclient_46824ade' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_plugin_7374548f from 'nuxt_plugin_plugin_7374548f' // Source: ./vuetify/plugin.js (mode: 'all')
import nuxt_plugin_plugin_72698d67 from 'nuxt_plugin_plugin_72698d67' // Source: ./composition-api/plugin.mjs (mode: 'all')
import nuxt_plugin_axios_328eb155 from 'nuxt_plugin_axios_328eb155' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_global_f21f4c18 from 'nuxt_plugin_global_f21f4c18' // Source: ../nuxt/plugins/global.ts (mode: 'client')
import nuxt_plugin_auth0_48649c1b from 'nuxt_plugin_auth0_48649c1b' // Source: ../nuxt/plugins/auth0.ts (mode: 'client')
import nuxt_plugin_polyfills_473a241f from 'nuxt_plugin_polyfills_473a241f' // Source: ../nuxt/plugins/polyfills.ts (mode: 'client')
import nuxt_plugin_libraries_3cea1f2c from 'nuxt_plugin_libraries_3cea1f2c' // Source: ../nuxt/plugins/libraries.ts (mode: 'client')
import nuxt_plugin_axios_5659cf26 from 'nuxt_plugin_axios_5659cf26' // Source: ../nuxt/plugins/axios.ts (mode: 'client')
import nuxt_plugin_meta_44539639 from 'nuxt_plugin_meta_44539639' // Source: ./composition-api/meta.mjs (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    return this.$root.$options.$nuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"titleTemplate":(title) => {
      return title;
    },"htmlAttrs":{"lang":"ja"},"meta":[{"charset":"utf-8"},{"name":"robots","content":"noindex, nofollow"},{"name":"viewport","content":"width=device-width,initial-scale=1,shrink-to-fit=no,viewport-fit=cover"},{"hid":"description","name":"description","content":""},{"name":"format-detection","content":"telephone=no"}],"script":[{"src":"https:\u002F\u002Fjs.stripe.com\u002Fv3"},{"hid":"gtag-src","async":true,"src":"https:\u002F\u002Fwww.googletagmanager.com\u002Fgtag\u002Fjs?id=G-DGHCJBCYXP"},{"hid":"gtag-inline","innerHTML":"window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'G-DGHCJBCYXP');"}],"__dangerouslyDisableSanitizersByTagID":{"gtag-inline":["innerHTML"]},"link":[{"rel":"stylesheet","type":"text\u002Fcss","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss?family=Roboto:100,300,400,500,700,900&display=swap"},{"rel":"stylesheet","type":"text\u002Fcss","href":"https:\u002F\u002Fcdn.jsdelivr.net\u002Fnpm\u002F@mdi\u002Ffont@latest\u002Fcss\u002Fmaterialdesignicons.min.css"}],"style":[]},

    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_42784c19 === 'function') {
    await nuxt_plugin_sentryserver_42784c19(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_46824ade === 'function') {
    await nuxt_plugin_sentryclient_46824ade(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_7374548f === 'function') {
    await nuxt_plugin_plugin_7374548f(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_72698d67 === 'function') {
    await nuxt_plugin_plugin_72698d67(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_328eb155 === 'function') {
    await nuxt_plugin_axios_328eb155(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_global_f21f4c18 === 'function') {
    await nuxt_plugin_global_f21f4c18(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_auth0_48649c1b === 'function') {
    await nuxt_plugin_auth0_48649c1b(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_polyfills_473a241f === 'function') {
    await nuxt_plugin_polyfills_473a241f(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_libraries_3cea1f2c === 'function') {
    await nuxt_plugin_libraries_3cea1f2c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_axios_5659cf26 === 'function') {
    await nuxt_plugin_axios_5659cf26(app.context, inject)
  }

  if (typeof nuxt_plugin_meta_44539639 === 'function') {
    await nuxt_plugin_meta_44539639(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, (err) => {
        // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
        if (!err._isRouter) return reject(err)
        if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    app,
    router
  }
}

export { createApp, NuxtError }
